import Fade from '@mui/material/Fade'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import React, { forwardRef, RefObject, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'

import { HandlerOf } from '../../helpers/typeHelper'
import { blueGray, cyanD1, dividerGray, midGray, royalBlue } from '../../theme/colors'
import { label3, uppercaseLabel2 } from '../../theme/typography'
import { TextInput } from '../TextInput/TextInput'

const CustomInput = function CustomInput(props: any) {
  const { inputProps, InputProps, ownerState, inputRef, ...other } = props

  return (
    <CustomInputWrap ref={InputProps?.ref}>
      <TextInput
        ref={inputRef}
        {...inputProps}
        {...(other as any)}
        noMargin
        placeholder={undefined}
        startAdornment={InputProps?.startAdornment}
      />
    </CustomInputWrap>
  )
}

const CustomInputWrap = styled.div`
  .MuiInputAdornment-positionStart {
    margin-left: 12px;
  }

  .MuiInputAdornment-positionStart svg {
    color: ${blueGray};
  }

  .MuiIconButton-edgeStart {
    margin-left: 0;
  }

  .MuiIconButton-edgeStart:hover {
    background: none;
  }
`

interface DatePickerProps {
  label?: string
  value?: any
  name?: string
  onChange?: HandlerOf<any>
  minDate?: string | Date | null
  maxDate?: string | Date | null
  disablePast?: boolean
  onError?: HandlerOf<any>
  error?: React.ReactElement | string | boolean
  warning?: string | React.ReactElement
  className?: string
  shouldDisableDate?: (date: Date, position?: 'start' | 'end') => boolean
}

export const DatePicker = forwardRef((props: DatePickerProps, ref) => {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(false)
  const { error, warning, className, ...pickerProps } = props

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        slots={{
          textField: CustomInput,
          // @ts-ignore This works but typings are broken here
          desktopTransition: Fade,
        }}
        slotProps={{
          inputAdornment: {
            position: 'start',
          },
          textField: {
            name: props.name,
            ref: ref as RefObject<HTMLDivElement>,
            onClick: () => setIsOpen(true),
            className,
            // @ts-ignore
            warning,
            error: error as any, // Our CustomInput needs string instead of boolean like material field
          },
        }}
        open={isOpen}
        reduceAnimations
        onAccept={() => setIsOpen(false)}
        onClose={() => setIsOpen(false)}
        {...pickerProps}
      />
      <DatePickerGlobalStyle />
    </LocalizationProvider>
  )
})

export const DatePickerGlobalStyle = createGlobalStyle`
  .MuiPickersPopper-root {
    .MuiPaper-root {
      box-shadow: 0 8px 8px rgba(0, 44, 177, 0.01);
      border: 1px solid ${cyanD1};
      border-radius: 3px;
    }

    .MuiDateCalendar-root {
      width: 370px;
      max-width: 100%;
      height: auto;
      max-height: 404px;
    }

    .MuiPickersSlideTransition-root {
      min-height: 280px;
    }

    .MuiPickersCalendarHeader-label {
      ${uppercaseLabel2};
      padding-left: 12px;
    }

    .MuiPickersCalendarHeader-labelContainer button {
      margin-top: -2px;
    }

    .MuiPickersArrowSwitcher-root {
      padding-right: 8px;

      svg {
        color: ${blueGray};
      }
    }

    .MuiDayCalendar-weekDayLabel {
      ${label3};
      color: ${midGray};
      width: 44px;
    }

    .MuiPickersDay-root {
      ${label3};
      width: 44px;
      min-width: 44px;
      height: 44px;
    }

    .MuiPickersDay-root.Mui-disabled {
      color: ${dividerGray};
    }

    .MuiPickersDay-dayWithMargin:focus {
      background-color: unset;
    }

    .MuiPickersDay-dayWithMargin:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }

    .MuiPickersDay-root.Mui-selected {
      background-color: ${cyanD1} !important;
    }

    .MuiPickersDay-root.Mui-selected:hover {
      background-color: ${royalBlue} !important;
    }

    .MuiYearCalendar-root {
      width: 100%;
      margin-top: -2px;
    }

    .MuiPickersYear-yearButton {
      ${label3};
    }

    .MuiPickersYear-yearButton.Mui-selected {
      background-color: ${cyanD1} !important;
    }

    .MuiPickersYear-yearButton.Mui-selected:hover {
      background-color: ${royalBlue} !important;
    }
  }
`
